import React, { useEffect, useState } from "react";
import { X, Share, MoreVertical, Download, Smartphone } from "lucide-react";
import { BrandMark } from "./BrandLogo";
import { toast } from "./ui/sonner";
import { isIOS, hasNativeInstall, promptInstall, shouldAutoShowA2hs, dismissA2hs } from "../lib/pwa";

// One-time "Add to Home Screen" tip for phones (auto after login; re-openable via the mobile more-menu)
export function InstallPrompt({ zh }) {
  const [open, setOpen] = useState(false);
  const [native, setNative] = useState(hasNativeInstall());
  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onReady = () => setNative(true);
    const onInstalled = () => setOpen(false);
    window.addEventListener("ali-a2hs-open", onOpen);
    window.addEventListener("ali-a2hs-ready", onReady);
    window.addEventListener("appinstalled", onInstalled);
    const t = shouldAutoShowA2hs() ? setTimeout(() => setOpen(true), 2500) : null;
    return () => { if (t) clearTimeout(t); window.removeEventListener("ali-a2hs-open", onOpen); window.removeEventListener("ali-a2hs-ready", onReady); window.removeEventListener("appinstalled", onInstalled); };
  }, []);
  if (!open) return null;

  const ios = isIOS();
  const install = async () => {
    const r = await promptInstall();
    if (r === "accepted") { toast.success(zh ? "已添加到主屏幕" : "Added to your home screen"); dismissA2hs(true); setOpen(false); }
    else if (r === "unavailable") toast(zh ? "请按下方步骤手动添加" : "Follow the steps below to add it manually");
  };
  const later = () => { dismissA2hs(false); setOpen(false); };
  const never = () => { dismissA2hs(true); setOpen(false); };
  const Step = ({ n, icon: Icon, text }) => (
    <li className="flex items-start gap-2 text-xs text-slate-600"><span className="h-5 w-5 shrink-0 rounded-full bg-brand-50 text-brand-600 text-[11px] font-bold flex items-center justify-center">{n}</span><span className="leading-5">{Icon && <Icon className="inline h-3.5 w-3.5 -mt-0.5 mr-1 text-slate-400" />}{text}</span></li>
  );

  return (
    <div data-testid="install-prompt" className="fixed inset-x-3 sm:inset-x-auto sm:right-6 sm:w-[380px] z-[70] a2hs-pos sheet-up">
      <div className="rounded-2xl border border-slate-200/80 bg-white shadow-lift p-4">
        <div className="flex items-start gap-3">
          <BrandMark size={40} testid="install-prompt-mark" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-800">{zh ? "把 Aligcus 添加到手机主屏幕" : "Add Aligcus to your home screen"}</p>
            <p className="text-xs text-slate-500 mt-0.5">{zh ? "全屏打开、带图标，像 App 一样一键进入工作台。" : "Full-screen, with an icon — open the workspace like a native app."}</p>
          </div>
          <button data-testid="install-prompt-close" onClick={later} className="h-7 w-7 -mr-1 -mt-1 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        {native ? (
          <button data-testid="install-prompt-native-btn" onClick={install} className="btn-brand mt-3 w-full !rounded-xl !py-2.5 text-sm font-bold inline-flex items-center justify-center gap-2"><Download className="h-4 w-4" />{zh ? "立即安装" : "Install now"}</button>
        ) : (
          <ol className="mt-3 space-y-1.5" data-testid="install-prompt-steps">
            {ios ? (<>
              <Step n={1} icon={Share} text={zh ? "点浏览器底部的「分享」按钮" : "Tap the Share button at the bottom of Safari"} />
              <Step n={2} icon={Smartphone} text={zh ? "选择「添加到主屏幕」" : "Choose “Add to Home Screen”"} />
              <Step n={3} text={zh ? "点右上角「添加」完成" : "Tap “Add” in the top-right corner"} />
            </>) : (<>
              <Step n={1} icon={MoreVertical} text={zh ? "点浏览器右上角的「⋮」菜单" : "Tap the ⋮ menu in the top-right of the browser"} />
              <Step n={2} icon={Smartphone} text={zh ? "选择「安装应用」或「添加到主屏幕」" : "Choose “Install app” or “Add to Home screen”"} />
            </>)}
          </ol>
        )}
        <div className="mt-3 flex items-center justify-end gap-3 text-xs">
          <button data-testid="install-prompt-never" onClick={never} className="text-slate-400 hover:text-slate-600">{zh ? "不再提示" : "Don't show again"}</button>
          <button data-testid="install-prompt-later" onClick={later} className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50">{zh ? "知道了" : "Got it"}</button>
        </div>
      </div>
    </div>
  );
}

export default InstallPrompt;
